import { ICON_WARNING } from '@repo/assets';
import { FlexWrap, Icon, Input, Typography } from '@repo/components';
import {
  CHN_BANK_ABBREVIATION_LIST,
  CHN_BANK_LIST,
  CHN_BANK_TRADITION_LIST,
} from '@repo/constants';
import { AccountInfo } from '@repo/types';
import { bankCardOwnershipInquiry } from '@repo/utils';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import styled, { css, keyframes } from 'styled-components';

type Props = {
  register: UseFormRegister<AccountInfo>;
  getValues: UseFormGetValues<AccountInfo>;
  setValue: UseFormSetValue<AccountInfo>;
  errors?: FieldErrors;
  disabled?: boolean;
  isHkgNation: boolean;
  setIsHkgNation?: Dispatch<SetStateAction<boolean>>;
  selectedGender: string;
  setSelectedGender: Dispatch<SetStateAction<string>>;
  isBankCardInquiry: boolean;
  setIsBankCardInquiry: Dispatch<SetStateAction<boolean>>;
  isRefundInfoPage?: boolean;
  paymentStatus?: string;
};

export const InfoCHN = ({
  errors,
  register,
  getValues,
  setValue,
  disabled,
  isHkgNation,
  setIsHkgNation,
  selectedGender,
  setSelectedGender,
  isBankCardInquiry,
  setIsBankCardInquiry,
  isRefundInfoPage,
  paymentStatus,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const onClickBankCardOwnership = async () => {
    setIsLoading(true);
    const cardInfo: any = await bankCardOwnershipInquiry(
      getValues('bankAccount') || '',
    );
    setIsLoading(false);

    if (cardInfo?.codeid !== '10000') {
      alert('银行验证失败，请重新输入。');
      setValue('bank', '');
      setIsBankCardInquiry(false);
      return;
    }

    if (cardInfo?.retdata?.cardtype !== '借记卡') {
      alert('输入您的借记卡号。不接受信用卡。');
      setValue('bank', '');
      setIsBankCardInquiry(false);
      return;
    }

    // 중국은행 간체
    const bankSimple = CHN_BANK_LIST.filter((item) =>
      item.includes(cardInfo.retdata.bankname),
    );
    // 중국은행 번체
    const bankTradition = CHN_BANK_TRADITION_LIST.filter((item) =>
      item.includes(cardInfo.retdata.bankname),
    );
    // 중국은행 약어
    const bankAbbreviation = CHN_BANK_ABBREVIATION_LIST.filter(
      (item) => item === cardInfo.retdata.abbreviation,
    );
    const bank = [...bankSimple, ...bankTradition, ...bankAbbreviation];
    if (bank.length === 0) {
      // \n6. 交通银行\n7. 光大银行\n8. 上海银行\n9. 临商银行 송금 가능 은행 삭제
      alert(
        '银行无法汇款。\n\n可以汇款的银行列表\n1. 中国銀行\n2. 中国工商银行\n3. 华夏银行\n4. 中国农业银行\n5. 中国建设银行\n\n如果您在输入信息时遇到困难,\n请通过以下电子邮件联系我们。\n客户服务 ktaxpay@gmail.com',
      );
      setValue('bank', '');
      setIsBankCardInquiry(false);
      return;
    }
    // 약어만 같을경우, chnBankabbreviationList와 bank값을 매칭 해서 "bank" 할당
    if (bankAbbreviation.length > 0 && bank.length === 1) {
      const index = CHN_BANK_ABBREVIATION_LIST.indexOf(bank[0]);
      setValue('bank', CHN_BANK_LIST[index]);
    } else {
      setValue('bank', bank[0]);
    }
    setIsBankCardInquiry(true);
  };

  const resetChnBankInfo = () => {
    setValue('bank', '');
    setIsBankCardInquiry(false);
  };

  return (
    <>
      <LabelWrapper>
        <Label>账号</Label>
        <RequiredDot>*</RequiredDot>
      </LabelWrapper>
      {isBankCardInquiry && (
        <Input
          placeholder='bank'
          {...register('bank')}
          error={errors?.bank}
          disabled={disabled || isBankCardInquiry}
          margin={[0, 0, 8, 0]}
        />
      )}
      <InputWrapper>
        <Input
          placeholder='Bank account'
          {...register('bankAccount', {})}
          type='number'
          error={errors?.bankAccount}
          disabled={
            disabled ||
            (paymentStatus !== 'PAYMENT_WAIT' && paymentStatus !== 'WAIT') ||
            isBankCardInquiry
          }
          style={{ padding: '14px 13px' }}
          onWheel={(event) => (event.target as HTMLElement).blur()}
          inputMode='numeric'
          onInput={(e) => {
            e.currentTarget.value = e.currentTarget.value.replace(/[^\d]/g, '');
          }}
        />
        {(isBankCardInquiry || (isRefundInfoPage && isBankCardInquiry)) && (
          <AuthText>已认证</AuthText>
        )}
      </InputWrapper>
      <Typography
        size='12px'
        lineHeight='20px'
        fontWeight='400'
        color='#A7A7A7'
        margin='8px 0 20px 0'
      >
        * 请输入银联绑定的借记卡卡号。
      </Typography>
      {isLoading ? (
        <Button isDisabled>
          <LoadingSpinner />
        </Button>
      ) : (
        <Button
          type='button'
          isDisabled={
            disabled ||
            (paymentStatus !== 'PAYMENT_WAIT' && paymentStatus !== 'WAIT')
          }
          disabled={
            disabled ||
            (paymentStatus !== 'PAYMENT_WAIT' && paymentStatus !== 'WAIT')
          }
          onClick={
            isBankCardInquiry ? resetChnBankInfo : onClickBankCardOwnership
          }
        >
          {isBankCardInquiry ? '修订' : '验证银行账户'}
        </Button>
      )}
      {!isRefundInfoPage && (
        <Typography
          size='13px'
          lineHeight='19.5px'
          fontWeight='400'
          color='#246CF6'
          margin='0 0 20px 0'
          cursor='pointer'
          onClick={() => setIsHkgNation && setIsHkgNation((v) => !v)}
        >
          Receive money to a Hong Kong account &gt;
        </Typography>
      )}
      <FlexWrap dir='column'>
        <LabelWrapper>
          <Label>性别</Label>
          <RequiredDot>*</RequiredDot>
        </LabelWrapper>
        <FlexWrap
          dir='column'
          justifyContent='center'
          gap='16px'
          margin='0 0 20px 0'
        >
          <RadioLabel>
            <input
              {...register('gender', {
                required: '这是必填项。',
              })}
              type='radio'
              value='FEMALE'
              onClick={() => setSelectedGender('FEMALE')}
              checked={selectedGender === 'FEMALE'}
              disabled={disabled}
            />
            Female 女性
          </RadioLabel>
          <RadioLabel>
            <input
              {...register('gender', {
                required: '这是必填项。',
              })}
              type='radio'
              value='MALE'
              onClick={() => setSelectedGender('MALE')}
              checked={selectedGender === 'MALE'}
              disabled={disabled}
            />
            Male 男性
          </RadioLabel>
        </FlexWrap>
        {errors?.gender && (
          <FlexWrap gap='8px'>
            <Icon imgUrl={ICON_WARNING} width='18px' height='18px' />
            <Typography color='#f13e4b' size='13px' fontWeight='400'>
              这是必填项。
            </Typography>
          </FlexWrap>
        )}
      </FlexWrap>
      <Input
        {...register('citizenId', {
          required: '这是必填项。',
          pattern: {
            value: /(\d){17}(\d|[Xx])/,
            message: '无效的公民身份号码。',
          },
        })}
        label='公民身份号码'
        maxLength={18}
        isRequired
        placeholder='Citizen ID Number'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.citizenId}
      />
      <Input
        {...register('stateProvinceRegion', {
          required: '这是必填项。',
          pattern: {
            value: isHkgNation ? /^\S.+$/ : /^\S[\u4e00-\u9fa50-9\s]*$/,
            message: '请用中文输入。',
          },
        })}
        label='省'
        isRequired
        placeholder='Province 仅限中文'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.stateProvinceRegion}
      />
      <Input
        {...register('city', {
          required: '这是必填项。',
          pattern: {
            value: isHkgNation ? /^\S.+$/ : /^\S[\u4e00-\u9fa50-9\s]*$/,
            message: '请用中文输入。',
          },
        })}
        label='市'
        isRequired
        placeholder='City 仅限中文'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.city}
      />
      <Input
        {...register('address', {
          required: '这是必填项。',
          pattern: {
            value: /^\S[\u4e00-\u9fa50-9\s,-]*$/,
            message: '这是必填项。',
          },
        })}
        label='区'
        isRequired
        placeholder='District 仅限中文'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.address}
      />
      <Input
        {...register('addressDetail', {
          pattern: {
            value: /^\S[\u4e00-\u9fa50-9\s,-]*$/,
            message: '这是必填项。',
          },
        })}
        label='详细地址'
        placeholder='Recipient address 仅限中文'
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.addressDetail}
      />
      <Input
        {...register('zipPostalCode', {
          required: '这是必填项。',
          pattern: {
            value: /^[a-zA-Z0-9]{6}$/,
            message: '请输入 6 个字符（仅限字母和数字）。',
          },
        })}
        label='邮政编码'
        isRequired
        placeholder='Post code'
        maxLength={6}
        margin={[20, 0, 0]}
        disabled={disabled}
        error={errors?.zipPostalCode}
      />
    </>
  );
};

const InputWrapper = styled.div`
  position: relative;
`;

const LabelWrapper = styled.div<{ marginTop?: string }>`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-top: ${(props) => props.marginTop && props.marginTop};
`;

const Label = styled.label`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  @media ${(props) => props.theme.mobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;
const RequiredDot = styled.div`
  color: #f13e4b;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  margin-left: 4px;
  padding-bottom: 6px;
`;

const RadioLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #212121;
`;

const Spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  animation: ${Spin} 600ms linear infinite;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  border: 5px solid #fff;
  margin: 0 auto;
  border-top: 5px solid #246cf6;
`;

const AuthText = styled.p`
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  font-size: 14px;
  line-height: 21px;
  font-weight: 600;
  color: #246cf6;
`;

const Button = styled.button<{
  isDisabled: boolean;
}>`
  width: 100%;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #fff;
  background-color: #246cf6;
  padding: 12px 0;
  text-align: center;
  border-radius: 8px;
  margin-bottom: 20px;
  cursor: pointer;
  ${(props) =>
    props.isDisabled &&
    css`
      background-color: #d3e2fd;
    `}
  :disabled {
    color: #fff;
    :hover {
      opacity: 1;
    }
  }
`;
